import { resolvePositionScene } from "./positionScene";
import { drawPositionPriceBadges } from "./positionPills";
import { drawPositionLabels } from "./drawPositionLabels";
import { drawEntryMarkers } from "./entryMarker";
import { hasPositionCards, positionFont, positionSizesFor } from "./positionConstants";

/**
 * Where the position lines are in a frame; the same for the WebGL layer and
 * the text layer.
 *
 * @param {object} engine - ChartEngine: positions, data, size, priceScale, textLayer, theme
 * @param {{ width: number, height: number }} pane
 * @returns {{ scene: object, sizes: object, shownPositions: object[] }}
 */
export const resolveEnginePositions = ({ positions, data, size, priceScale, textLayer, theme }, pane) => {
  const shownPositions = positions.shownFor(data);
  const sizes = positionSizesFor(size.width);
  const scene = resolvePositionScene(shownPositions, {
    priceScale,
    pane,
    sizes,
    measure: textLayer.measure,
    pillFont: positionFont(sizes.pillFontSize, theme.fontFamily),
    withCards: hasPositionCards(size.width),
  });

  return { scene, sizes, shownPositions };
};

/**
 * Text layer of the positions in two steps: the price badges go under the
 * last price badge, which covers them, the pills, cards and entry markers above
 * everything else. Resolved from the current positions on every draw, so a
 * new PnL needs no WebGL frame.
 *
 * @param {object} engine - ChartEngine: positions, data, size, scales, series, textLayer, theme
 * @param {{ pane: { width: number, height: number }, priceAxisWidth: number }} frame - from drawFrame
 * @returns {{ drawBadges: (fixedRects: object[]) => void, drawLabels: () => void }}
 */
export const preparePositionsText = (engine, { pane, priceAxisWidth }) => {
  const { positions, data, priceScale, timeScale, series, textLayer, theme } = engine;
  const { scene, sizes, shownPositions } = resolveEnginePositions(engine, pane);

  return {
    /** Entry price badges, under the last price badge drawn after them. */
    drawBadges: () =>
      drawPositionPriceBadges(textLayer, scene.lines, {
        pane,
        priceAxisWidth,
        decimals: data.priceFormat.decimals,
        theme,
      }),

    drawLabels: () => {
      drawEntryMarkers(textLayer, shownPositions, {
        store: data.store,
        barDurationMs: data.barDurationMs,
        series,
        timeScale,
        priceScale,
        pane,
        color: theme.colors.positionEntry.css,
      });
      positions.setCloseButtons(
        drawPositionLabels(textLayer, scene, { pane, sizes, theme, highlight: positions.highlight }),
      );
    },
  };
};
